import { lazy, Suspense, useMemo, useState } from 'react';
import { downloadSTEPFile } from '@/utils/downloadUtils';
import { useRegisterDownload } from '@/components/download/useRegisterDownload';
import { UniversalDownloadMenu } from '@/components/download/UniversalDownloadMenu';
import type { DownloadFormat } from '@/components/download/DownloadTypes';

import {
  openScanSession,
  type ScanJobResult,
  type ScanSession,
} from '@/services/scanToCad';

// Lazy so the workbench's three.js/R3F bundle stays OFF the always-loaded path
// (the Lazy3D boundary — this panel is reachable from the eager sidebar graph).
// It loads only when the user opens the workbench.
const ScanToCadCanvas = lazy(() =>
  import('./ScanToCadCanvas').then((m) => ({ default: m.ScanToCadCanvas })),
);

export interface ScanToCadResultProps {
  result: ScanJobResult;
  /** The job id — opening the workbench seeds a live editing session from it. */
  jobId: string | null;
  onReset: () => void;
}

const GRADE_COPY: Record<string, string> = {
  A: 'Excellent — parametric, trustworthy unattended.',
  B: 'Good — parametric, trustworthy unattended.',
  C: 'Fair — reconstructed; review recommended.',
  D: 'Degraded — mesh-body fallback; review required.',
  F: 'Failed to reconstruct a valid solid.',
};

// Result panel: the STEP download (this is the first place in the app that wires
// the long-existing but unused downloadSTEPFile helper), plus the recon grade +
// deviation. Scan-to-CAD outputs STEP only — no mesh export offered here.
export function ScanToCadResult({
  result,
  jobId,
  onReset,
}: ScanToCadResultProps) {
  const stepB64 = result.stepB64;
  const [session, setSession] = useState<ScanSession | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const hasCanvas = !!result.overlay && result.overlay.regions.length > 0;

  const openWorkbench = async () => {
    if (!jobId) {
      setOpenError('No job to open a workbench for.');
      return;
    }
    setOpening(true);
    setOpenError(null);
    try {
      setSession(await openScanSession(jobId));
    } catch (e) {
      setOpenError(
        e instanceof Error ? e.message : 'Could not open the workbench.',
      );
    } finally {
      setOpening(false);
    }
  };

  // Register STEP into the ONE download registry so scan-to-CAD's download is the
  // same universal white menu as every other model. This route has no feature
  // tree, so the menu is hosted here directly (below).
  const downloadFormats = useMemo<DownloadFormat[] | null>(
    () =>
      stepB64
        ? [
            {
              id: 'step',
              label: '.STEP',
              hint: 'CAD B-rep (Solidworks/Fusion)',
              run: () => downloadSTEPFile(stepB64),
            },
          ]
        : null,
    [stepB64],
  );
  useRegisterDownload(downloadFormats, true);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {result.grade && (
          <span
            className="flex h-6 min-w-6 items-center justify-center rounded-md bg-[#107d40] px-1.5 text-xs font-semibold text-black"
            title="Reconstruction grade"
          >
            {result.grade}
          </span>
        )}
        <p className="text-xs text-telekinesis-neutral-50">
          {result.grade
            ? (GRADE_COPY[result.grade] ?? 'Reconstruction complete.')
            : 'Reconstruction complete.'}
        </p>
      </div>

      {typeof result.deviationMm === 'number' && (
        <p className="text-[11px] text-telekinesis-neutral-400">
          Deviation vs scan: {result.deviationMm.toFixed(2)} mm (95th pct)
        </p>
      )}

      {hasCanvas && (
        <button
          type="button"
          onClick={openWorkbench}
          disabled={opening}
          className="w-full rounded-md bg-[#107d40] px-3 py-2 text-xs font-medium text-black hover:brightness-110 disabled:opacity-50"
        >
          {opening ? 'Opening workbench…' : 'Open Recon Workbench'}
        </button>
      )}
      {openError && <p className="text-[11px] text-red-400">{openError}</p>}

      {/* The ONE universal download button (white, always a menu). */}
      <UniversalDownloadMenu />

      <button
        type="button"
        onClick={onReset}
        className="w-full rounded-md border border-telekinesis-neutral-700 px-3 py-2 text-[11px] text-telekinesis-neutral-50 hover:border-[#107d40] hover:text-[#107d40]"
      >
        Reconstruct another scan
      </button>

      {session && (
        <Suspense fallback={null}>
          <ScanToCadCanvas
            result={result}
            session={session}
            onClose={() => setSession(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
